import {openAiActionLoadingModal} from '@Modals/ai-action-loading-modal';
import {PublishingOption} from '@Components/ai-chat-window/components/publishing-options/publishing-options.types';
import {applyLetterCase, getUniqueString, LetterCase} from '@Utils/string.util';
import type {NavigationAction} from '@Modals/ai-action-loading-modal/ai-action-loading-modal.types';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';

let currentActiveSessionId = '';

export const isCurrentModalSession = (sessionId: string): boolean => {
  return sessionId === currentActiveSessionId;
};

export const invalidateModalSession = (): void => {
  currentActiveSessionId = '';
};

interface PublishingModalConfig {
  title: string;
  loadingText: string;
  completedText: string;
  ctaText: string;
}

const getPublishingModalConfig = (option: PublishingOption): PublishingModalConfig => {
  switch (option) {
    case PublishingOption.PUBLISH_TO_SOCIAL_MEDIA:
      return {
        title: window.i18next.t('pmwjs_post_social_media'),
        loadingText: window.i18next.t('pmwjs_preparing_social_post'),
        completedText: window.i18next.t('pmwjs_ready_to_post'),
        ctaText: window.i18next.t('pmwjs_open_social_media_post'),
      };
    case PublishingOption.PROMOTE_AS_EVENT:
      return {
        title: window.i18next.t('pmwjs_promote_as_event'),
        loadingText: window.i18next.t('pmwjs_preparing_event'),
        completedText: window.i18next.t('pmwjs_event_ready'),
        ctaText: window.i18next.t('pmwjs_open_event'),
      };
    case PublishingOption.PUBLISH_AS_EMAIL_CAMPAIGN:
      return {
        title: window.i18next.t('pmwjs_publish_email_campaign'),
        loadingText: window.i18next.t('pmwjs_preparing_email'),
        completedText: window.i18next.t('pmwjs_email_ready'),
        ctaText: window.i18next.t('pmwjs_open_email_campaign'),
      };
  }
};

export const openEditorLoadingModalAndGetSessionId = (): string => {
  currentActiveSessionId = getUniqueString();
  openAiActionLoadingModal({
    title: applyLetterCase(window.i18next.t('pmwjs_edit_image_in_editor'), LetterCase.SENTENCE_CASE),
    loadingText: window.i18next.t('pmwjs_preparing_editor'),
    completedText: window.i18next.t('pmwjs_design_is_ready'),
    ctaText: window.i18next.t('pmwjs_edit_image_in_editor'),
  });

  return currentActiveSessionId;
};

export const showEditorReadyModal = (navigationUrl: string): void => {
  openAiActionLoadingModal({
    title: applyLetterCase(window.i18next.t('pmwjs_edit_image_in_editor'), LetterCase.SENTENCE_CASE),
    loadingText: window.i18next.t('pmwjs_preparing_editor'),
    completedText: window.i18next.t('pmwjs_design_is_ready'),
    ctaText: window.i18next.t('pmwjs_edit_image_in_editor'),
    navigationAction: {
      url: navigationUrl,
      openInNewTab: true,
    },
  });
};

export const openVideoEditorLoadingModalAndGetSessionId = (): string => {
  currentActiveSessionId = getUniqueString();
  openAiActionLoadingModal({
    title: applyLetterCase(window.i18next.t('pmwjs_edit_video_in_editor'), LetterCase.SENTENCE_CASE),
    loadingText: window.i18next.t('pmwjs_preparing_video_editor'),
    completedText: window.i18next.t('pmwjs_design_is_ready'),
    ctaText: window.i18next.t('pmwjs_edit_video_in_editor'),
  });

  return currentActiveSessionId;
};

export const showVideoEditorReadyModal = (navigationUrl: string): void => {
  openAiActionLoadingModal({
    title: applyLetterCase(window.i18next.t('pmwjs_edit_video_in_editor'), LetterCase.SENTENCE_CASE),
    loadingText: window.i18next.t('pmwjs_preparing_video_editor'),
    completedText: window.i18next.t('pmwjs_design_is_ready'),
    ctaText: window.i18next.t('pmwjs_edit_video_in_editor'),
    navigationAction: {
      url: navigationUrl,
      openInNewTab: true,
    },
  });
};

export const openShareLoadingModalAndGetSessionId = (): string => {
  currentActiveSessionId = getUniqueString();

  openAiActionLoadingModal({
    title: applyLetterCase(window.i18next.t('pmwjs_share_design'), LetterCase.SENTENCE_CASE),
    loadingText: window.i18next.t('pmwjs_preparing_share'),
    completedText: window.i18next.t('pmwjs_ready_to_share'),
    ctaText: window.i18next.t('pmwjs_open_sharing_options'),
  });

  return currentActiveSessionId;
};

export const openPublishingLoadingModalAndGetSessionId = (option: PublishingOption): string => {
  currentActiveSessionId = getUniqueString();

  const config = getPublishingModalConfig(option);
  openAiActionLoadingModal({
    title: applyLetterCase(config.title, LetterCase.SENTENCE_CASE),
    loadingText: config.loadingText,
    completedText: config.completedText,
    ctaText: config.ctaText,
  });

  return currentActiveSessionId;
};

export const showPublishingReadyModal = (option: PublishingOption, navigationAction: NavigationAction): void => {
  const config = getPublishingModalConfig(option);
  openAiActionLoadingModal({
    title: applyLetterCase(config.title, LetterCase.SENTENCE_CASE),
    loadingText: config.loadingText,
    completedText: config.completedText,
    ctaText: config.ctaText,
    navigationAction,
  });
};

export const closeAiActionLoadingModal = (): void => {
  window.PMW.redux.store.dispatch(closeModal(MODALS_IDS.AI_ACTION_LOADING_MODAL));
  invalidateModalSession();
};
